import restClient from "api/restClient";

/** Endpoint of the side-effect-free workspace-mail egress preview (backend
 * `POST /egress_policy/mail-recipient-check`). */
export const egressMailCheckPath = "/egress_policy/mail-recipient-check";

export interface EgressEmailVerdict {
    email: string;
    verdict: "allow" | "deny";
    /** The deciding policy, or null when the workspace default decided. */
    policy_name: string | null;
}

export interface EgressAffectedUser {
    id: string;
    name: string | null;
    email: string;
    policy_name: string | null;
    /** True when the address is allowed without the assessed policy. */
    caused_by_policy: boolean;
}

export interface EgressMailCheckResult {
    emails: EgressEmailVerdict[];
    users?: EgressAffectedUser[];
}

export const checkEgressMailRecipients = (emails: string[]): Promise<EgressMailCheckResult> =>
    restClient
        .post(egressMailCheckPath, { emails })
        .then((response) => response?.data as EgressMailCheckResult);

export const checkEgressPolicyUserImpact = (policyId: string): Promise<EgressMailCheckResult> =>
    restClient
        .post(egressMailCheckPath, { policy_id: policyId })
        .then((response) => response?.data as EgressMailCheckResult);

/** Loose shape check: only ask the backend once the input looks like an address. */
export const looksLikeEmail = (value: unknown): value is string =>
    typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const policyLabel = (policyName: string | null) =>
    policyName ? `the egress policy "${policyName}"` : "the workspace's default egress setting";

/** Warning text for a denied address, or null when mail to it is allowed. */
export const deniedEmailMessage = (
    verdict: EgressEmailVerdict | undefined,
    consequence: string
): string | null => {
    if (!verdict || verdict.verdict !== "deny") {
        return null;
    }
    return `Email to ${verdict.email} is blocked by ${policyLabel(verdict.policy_name)}. ${consequence}`;
};

const describeUsers = (users: EgressAffectedUser[]) =>
    users.map((user) => (user.name ? `${user.name} (${user.email})` : user.email)).join(", ");

/**
 * Warning text for an egress policy that blocks email to existing users, or
 * null when it blocks none. `isEnabled` decides the tense: an enabled policy
 * blocks mail now; a disabled one would block it once enabled.
 */
export const policyUserImpactMessage = (
    users: EgressAffectedUser[] | undefined,
    isEnabled: boolean
): string | null => {
    const caused = (users ?? []).filter((user) => user.caused_by_policy);
    if (!caused.length) {
        return null;
    }
    const count = caused.length === 1 ? "1 existing user" : `${caused.length} existing users`;
    const lead = isEnabled
        ? `This policy blocks email to ${count}`
        : `If you enable this policy, it will block email to ${count}`;
    return (
        `${lead}: ${describeUsers(caused)}. ` +
        "They will not receive invitations, password resets or notifications."
    );
};
